/* G&G portfolio editor (pfe-*), inside the author portal.
 *
 * The designer manages their own showcase here: add a project, attach a cover
 * and spreads, reorder them, edit anything, send it for review.
 *
 * Shape borrowed from author-submit.js on purpose - same custom dropdown, same
 * dropzone, same thumbnail with a progress ring - so a designer who has used
 * the submission form already knows this one. The code is its own copy rather
 * than a shared import: theme assets have no module system, and pulling in the
 * submission form's 1100 lines to reuse two widgets would cost every visitor.
 *
 * Deliberate choices:
 *   - The rights checkbox is NOT pre-ticked and the form will not submit
 *     without it. Most of a freelancer's portfolio is legally owned by their
 *     previous clients, so a warranty defaulted to "yes" is worth nothing.
 *   - Editing an approved project sends it back for review. The server enforces
 *     that; the note here just means nobody is surprised by it.
 *   - A NEW project stages its files in the browser and uploads them with the
 *     save, because there is no project to attach them to yet. An EXISTING one
 *     uploads on drop, so the author sees progress immediately.
 */
(function () {
  'use strict';

  var API = '/apps/proxy/api/storefront/authors/portfolio';
  var THUMB = '/apps/proxy/api/storefront/authors/thumb';
  var MAX_IMAGES = 12;                    // matches the service-layer limit
  var MAX_BYTES = 8 * 1024 * 1024;        // matches the service-layer limit
  var IMG_RE = /^image\/(jpeg|png|webp)$/i;

  function inEditor() {
    try { return !!(window.Shopify && window.Shopify.designMode); } catch (e) { return false; }
  }
  function el(tag, opts) {
    var n = document.createElement(tag);
    if (opts) {
      if (opts.className) n.className = opts.className;
      if (opts.text != null) n.textContent = String(opts.text);
      if (opts.html != null) n.innerHTML = opts.html;
      if (opts.attrs) Object.keys(opts.attrs).forEach(function (k) { n.setAttribute(k, opts.attrs[k]); });
    }
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function thumbUrl(url, w) { return THUMB + '?u=' + encodeURIComponent(url) + '&w=' + w; }

  function json(body) {
    return fetch(API, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(read).catch(function () {
      // Never rejects. A rejection here skipped the .then that unlocks the
      // form, so one dropped connection left Save disabled with a spinner and
      // no message, and only a reload recovered it.
      return { ok: false, data: { error: 'Could not reach the server. Try again.' } };
    });
  }
  function read(r) {
    return r.json().catch(function () { return {}; }).then(function (d) { return { ok: r.ok, data: d }; });
  }
  // Multipart goes through XHR, not fetch: fetch cannot report upload progress,
  // and a designer pushing 12 large spreads needs to see that it is moving.
  function upload(form, onProgress) {
    return new Promise(function (resolve) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', API, true);
      xhr.withCredentials = true;
      if (xhr.upload && onProgress) {
        xhr.upload.addEventListener('progress', function (e) {
          if (e.lengthComputable) onProgress(e.loaded / e.total);
        });
      }
      xhr.addEventListener('load', function () {
        var d = {}; try { d = JSON.parse(xhr.responseText) || {}; } catch (e) {}
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, data: d });
      });
      xhr.addEventListener('error', function () { resolve({ ok: false, data: { error: 'Network error' } }); });
      // Without this the promise never settles on an abort (Escape in Firefox,
      // some extensions, a connection change) and every guarded control on the
      // page stays dead for the rest of the session.
      xhr.addEventListener('abort', function () { resolve({ ok: false, data: { error: 'Upload cancelled' } }); });
      xhr.addEventListener('timeout', function () { resolve({ ok: false, data: { error: 'Request timed out' } }); });
      xhr.send(form);
    });
  }

  var STATUS_TEXT = {
    draft: 'Draft',
    pending: 'In review',
    approved: 'Live',
    rejected: 'Needs changes',
    hidden: 'Hidden',
  };
  var STATUS_HINT = {
    draft: 'Only you can see it. Send it for review when it is ready.',
    pending: 'We are looking at it.',
    approved: 'On your public page.',
    rejected: 'Fix what is noted below and send it again.',
    hidden: 'Taken off the public page by the studio.',
  };

  // ── Custom select: one panel open at a time, closes on an outside click ──
  var openPanels = [];
  document.addEventListener('click', function (e) {
    openPanels.slice().forEach(function (cs) { if (!cs.root.contains(e.target)) cs.close(); });
  });

  function customSelect(opts) {
    var options = opts.options || [];
    var multiple = !!opts.multiple;
    var selected = multiple
      ? (Array.isArray(opts.value) ? opts.value.slice() : [])
      : (opts.value || '');
    var searchable = opts.searchable != null ? opts.searchable : options.length > 7;

    var root = el('div', { className: 'pfe-cs' });
    var toggle = el('button', { className: 'pfe-cs-toggle', attrs: { type: 'button', 'aria-haspopup': 'listbox', 'aria-expanded': 'false' } });
    var label = el('span', { className: 'pfe-cs-label' });
    toggle.appendChild(label);
    toggle.appendChild(el('span', { className: 'pfe-cs-chevron', attrs: { 'aria-hidden': 'true' } }));
    var panel = el('div', { className: 'pfe-cs-panel', attrs: { role: 'listbox' } });
    panel.hidden = true;
    var list = el('div', { className: 'pfe-cs-list' });
    var search = null;
    if (searchable) {
      search = el('input', { className: 'pfe-cs-search', attrs: { type: 'text', placeholder: 'Search...' } });
      panel.appendChild(search);
      search.addEventListener('input', function () { renderList(search.value.toLowerCase()); });
      search.addEventListener('click', function (e) { e.stopPropagation(); });
    }
    panel.appendChild(list);
    root.appendChild(toggle); root.appendChild(panel);

    function labelFor(v) {
      for (var i = 0; i < options.length; i++) if (options[i].value === v) return options[i].label;
      return v;
    }
    function syncLabel() {
      var empty = multiple ? !selected.length : !selected;
      if (empty) { label.textContent = opts.placeholder || 'Select'; label.classList.add('is-placeholder'); return; }
      label.classList.remove('is-placeholder');
      if (!multiple) { label.textContent = labelFor(selected); return; }
      // Two names fit on the control; past that a count reads better than a
      // list cut off mid-word.
      label.textContent = selected.length <= 2
        ? selected.map(labelFor).join(', ')
        : selected.length + ' selected';
    }
    function renderList(filter) {
      list.innerHTML = '';
      var any = false;
      options.forEach(function (o) {
        if (filter && o.label.toLowerCase().indexOf(filter) === -1) return;
        any = true;
        var sel = multiple ? selected.indexOf(o.value) !== -1 : selected === o.value;
        var item = el('div', {
          className: 'pfe-cs-option' + (sel ? ' is-selected' : ''),
          attrs: { role: 'option', tabindex: '-1', 'aria-selected': sel ? 'true' : 'false' },
        });
        if (multiple) item.appendChild(el('span', { className: 'pfe-cs-check' }));
        item.appendChild(el('span', { text: o.label }));
        item.addEventListener('click', function (e) {
          e.stopPropagation();
          if (multiple) {
            // Stays open: picking collaborators usually means picking several,
            // and closing after each one would mean reopening for every name.
            var at = selected.indexOf(o.value);
            if (at === -1) selected.push(o.value); else selected.splice(at, 1);
            renderList(filter || ''); syncLabel();
          } else {
            selected = o.value; syncLabel(); close(); toggle.focus();
          }
          if (opts.onChange) opts.onChange(multiple ? selected.slice() : selected);
        });
        list.appendChild(item);
      });
      if (!any) list.appendChild(el('div', { className: 'pfe-cs-empty', text: 'No matches' }));
    }
    function optionEls() { return Array.prototype.slice.call(list.querySelectorAll('.pfe-cs-option')); }
    function focusOption(i) { var o = optionEls(); if (!o.length) return; o[Math.max(0, Math.min(i, o.length - 1))].focus(); }
    function moveFocus(d) {
      var o = optionEls(); if (!o.length) return;
      var cur = o.indexOf(document.activeElement);
      focusOption(cur === -1 ? (d > 0 ? 0 : o.length - 1) : cur + d);
    }
    function open() {
      panel.hidden = false; root.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true'); openPanels.push(api);
      if (search) { search.value = ''; renderList(''); setTimeout(function () { search.focus(); }, 0); }
      else setTimeout(function () { focusOption(0); }, 0);
    }
    function close() {
      panel.hidden = true; root.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      var i = openPanels.indexOf(api); if (i !== -1) openPanels.splice(i, 1);
    }
    toggle.addEventListener('click', function (e) { e.stopPropagation(); if (panel.hidden) open(); else close(); });
    toggle.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (panel.hidden) open(); else moveFocus(e.key === 'ArrowUp' ? -1 : 1); }
      else if (e.key === 'Escape') { if (!panel.hidden) close(); }
    });
    list.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); moveFocus(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); moveFocus(-1); }
      else if (e.key === 'Home') { e.preventDefault(); focusOption(0); }
      else if (e.key === 'End') { e.preventDefault(); focusOption(optionEls().length - 1); }
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        var a = document.activeElement;
        if (a && a.classList.contains('pfe-cs-option')) a.click();
      } else if (e.key === 'Escape') { e.preventDefault(); close(); toggle.focus(); }
      else if (e.key === 'Tab') { close(); }
    });
    if (search) {
      search.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); focusOption(0); }
        else if (e.key === 'Escape') { e.preventDefault(); close(); toggle.focus(); }
        else if (e.key === 'Enter') { e.preventDefault(); var f = optionEls()[0]; if (f) f.click(); }
      });
    }

    var api = {
      root: root, close: close,
      getValue: function () { return multiple ? selected.slice() : selected; },
      // renderList too: with 7 options the list is not searchable, so it is
      // built once and never rebuilt. Without this the label said "Branding"
      // while a different option kept is-selected and aria-selected="true".
      setValue: function (v) {
        selected = multiple ? (Array.isArray(v) ? v.slice() : []) : (v || '');
        syncLabel(); renderList('');
      },
      /** The peer roster arrives after the control is built. */
      setOptions: function (next) { options = next || []; syncLabel(); renderList(''); },
      /** For the chips, which spell out what the control shows as a count. */
      labelFor: labelFor,
    };
    renderList(''); syncLabel();
    if (opts.host) opts.host.appendChild(root);
    return api;
  }

  // Native number spinners are unstyleable, so hide them and draw our own.
  function enhanceNumber(input) {
    if (!input || input.parentNode.classList.contains('pfe-num')) return;
    var wrap = el('span', { className: 'pfe-num' });
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
    var steps = el('span', { className: 'pfe-num-steps' });
    ['up', 'down'].forEach(function (dir) {
      var b = el('button', {
        className: 'pfe-num-step pfe-num-' + dir,
        attrs: { type: 'button', tabindex: '-1', 'aria-label': dir === 'up' ? 'Increase' : 'Decrease' },
      });
      b.addEventListener('click', function () {
        if (dir === 'up') input.stepUp(); else input.stepDown();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
      steps.appendChild(b);
    });
    wrap.appendChild(steps);
  }

  function init(root) {
    if (root.getAttribute('data-pfe-preview') === '1') return;
    var inner = root.querySelector('.pfe-inner');
    if (!inner) return;

    var $ = function (s) { return inner.querySelector(s); };
    var els = {
      banner: $('[data-pfe-banner]'),
      list: $('[data-pfe-list]'),
      empty: $('[data-pfe-empty]'),
      formTitle: $('[data-pfe-form-title]'),
      title: $('[data-pfe-title]'),
      titleCount: $('[data-pfe-title-count]'),
      serviceHost: $('[data-pfe-service-host]'),
      role: $('[data-pfe-role]'),
      year: $('[data-pfe-year]'),
      client: $('[data-pfe-client]'),
      clientPublic: $('[data-pfe-client-public]'),
      description: $('[data-pfe-description]'),
      descCount: $('[data-pfe-desc-count]'),
      sourceHost: $('[data-pfe-source-host]'),
      sourceHint: $('[data-pfe-source-hint]'),
      creditsHost: $('[data-pfe-credits-host]'),
      creditsChips: $('[data-pfe-credits-chips]'),
      drop: $('[data-pfe-drop]'),
      files: $('[data-pfe-files]'),
      thumbs: $('[data-pfe-thumbs]'),
      thumbsHint: $('[data-pfe-thumbs-hint]'),
      rights: $('[data-pfe-rights]'),
      msg: $('[data-pfe-msg]'),
      submit: $('[data-pfe-submit]'),
      cancel: $('[data-pfe-cancel]'),
      editNote: $('[data-pfe-edit-note]'),
    };
    if (!els.list || !els.submit) return;

    var labels = {
      addTitle: root.getAttribute('data-pfe-l-add') || 'Add a project',
      editTitle: root.getAttribute('data-pfe-l-edit') || 'Edit project',
      addBtn: root.getAttribute('data-pfe-l-addbtn') || 'Add project',
      saveBtn: root.getAttribute('data-pfe-l-savebtn') || 'Save changes',
      servicePh: root.getAttribute('data-pfe-l-service-ph') || 'Not sure / other',
      // %s is the owner's name. A placeholder rather than concatenation, so a
      // merchant can put the name where their language wants it.
      byOther: root.getAttribute('data-pfe-l-by-other') || 'Added by %s',
      theirsHint: root.getAttribute('data-pfe-l-theirs') || 'Only they can change it',
    };

    var projects = [];      // last server state
    var editingId = null;   // null = the form is creating
    var staged = [];        // { file, url } queued for a project that does not exist yet
    var busy = false;
    var uploading = 0;      // files in flight, so the limit counts them too
    // Has the designer typed anything since the last programmatic fill? Only
    // then is it worth asking before throwing their work away.
    var dirty = false;

    // ── Controls ──
    // No blank "pick one" row any more: this is a multi-select, and picking
    // nothing is expressed by picking nothing.
    var serviceOptions = [];
    (root.getAttribute('data-pfe-services') || '').split(',').forEach(function (s) {
      var v = s.trim();
      if (v) serviceOptions.push({ value: v, label: v });
    });
    // One project is often several services at once: a bottle that was modelled
    // AND rendered belongs on both service pages, and a single answer forced
    // the designer to be wrong on one of them.
    var serviceCS = customSelect({
      host: els.serviceHost, options: serviceOptions, multiple: true, value: [],
      placeholder: labels.servicePh,
    });

    var sourceOptions = [
      { value: 'own', label: root.getAttribute('data-pfe-l-own') || 'My own project' },
      { value: 'studio', label: root.getAttribute('data-pfe-l-studio') || 'Made through this studio' },
    ];
    // The hint under the answer, not a permanent line: it is only true for
    // studio work, and a note that is always there stops being read.
    function syncSourceHint() {
      if (!els.sourceHint) return;
      els.sourceHint.hidden = sourceCS.getValue() !== 'studio';
    }
    var sourceCS = customSelect({
      host: els.sourceHost, options: sourceOptions, value: 'own', searchable: false,
      onChange: syncSourceHint,
    });

    // Co-authors. The roster is fetched once, separately from the projects: it
    // is the same for every project, so shipping it with each one would repeat
    // it forty times.
    var creditsCS = null;
    if (els.creditsHost) {
      creditsCS = customSelect({
        host: els.creditsHost,
        options: [],
        multiple: true,
        searchable: true,
        value: [],
        placeholder: root.getAttribute('data-pfe-l-credits-ph') || 'Nobody else',
        onChange: renderChips,
      });
      fetch(API + '?peers=1', { credentials: 'same-origin' }).then(read).then(function (res) {
        if (!res.ok) return;
        var peers = (res.data.peers || []).map(function (p) {
          return { value: p.id, label: p.displayName };
        });
        creditsCS.setOptions(peers);
        // The form may already be editing a project by the time this lands, so
        // repaint the chips against the names we can now resolve.
        renderChips();
      }).catch(function () { /* the picker just stays empty */ });
    }

    /** The picked collaborators, spelled out under a control that shows a count. */
    function renderChips() {
      if (!els.creditsChips || !creditsCS) return;
      var ids = creditsCS.getValue();
      els.creditsChips.innerHTML = '';
      ids.forEach(function (id) {
        var chip = el('span', { className: 'pfe-chip' });
        chip.appendChild(el('span', { text: creditsCS.labelFor(id) }));
        var x = el('button', {
          className: 'pfe-chip-x', text: '×',
          attrs: { type: 'button', 'aria-label': 'Remove ' + creditsCS.labelFor(id) },
        });
        x.addEventListener('click', function () {
          creditsCS.setValue(ids.filter(function (v) { return v !== id; }));
          dirty = true;
          renderChips();
        });
        chip.appendChild(x);
        els.creditsChips.appendChild(chip);
      });
    }

    enhanceNumber(els.year);

    function counter(input, out, max) {
      if (!input || !out) return;
      var sync = function () { out.textContent = (input.value || '').length + ' / ' + max; };
      input.addEventListener('input', sync);
      sync();
    }
    counter(els.title, els.titleCount, 160);
    counter(els.description, els.descCount, 2000);

    // `input` only, not `change`: a programmatic fill dispatches `input` on the
    // two counted fields, so `dirty` is cleared again at the end of every
    // resetForm/startEdit rather than being armed by them.
    ['title', 'role', 'year', 'client', 'description'].forEach(function (k) {
      if (els[k]) els[k].addEventListener('input', function () { dirty = true; });
    });
    if (els.clientPublic) els.clientPublic.addEventListener('change', function () { dirty = true; });

    // ── Messaging ──
    function banner(text, kind) {
      if (!els.banner) return;
      if (!text) { els.banner.hidden = true; return; }
      els.banner.textContent = text;
      els.banner.className = 'pfe-banner' + (kind ? ' pfe-banner--' + kind : '');
      els.banner.hidden = false;
    }
    function msg(text, kind) {
      if (!els.msg) return;
      els.msg.textContent = text || '';
      els.msg.className = 'pfe-foot-msg' + (kind ? ' is-' + kind : '');
    }
    // Visual only. It used to also write `busy`, which guarded() owns, so a
    // finishing upload cleared the guard of a save that was still in flight and
    // a second click fired a second request.
    function lock(on) {
      els.submit.disabled = on;
      if (on) els.submit.classList.add('is-loading'); else els.submit.classList.remove('is-loading');
    }

    // ── Staged files (create mode) ──
    function rejectFile(f) {
      if (!IMG_RE.test(f.type)) return f.name + ': only JPG, PNG or WebP';
      if (f.size > MAX_BYTES) return f.name + ' is over 8 MB';
      return null;
    }
    function currentImageCount() {
      // `uploading` matters: the stored count only refreshes when the upload
      // finishes, so dropping 8 and then 8 more passed the check twice and the
      // server silently discarded the overflow with no message.
      if (!editingId) return staged.length;
      var p = byId(editingId);
      return (p ? p.images.length : 0) + uploading;
    }
    function acceptFiles(fileList) {
      var files = Array.prototype.slice.call(fileList || []);
      if (!files.length) return;
      var room = MAX_IMAGES - currentImageCount();
      if (room <= 0) { msg('That is the limit of ' + MAX_IMAGES + ' images for one project.', 'error'); return; }
      var bad = [];
      var good = [];
      files.forEach(function (f) {
        var why = rejectFile(f);
        if (why) bad.push(why); else good.push(f);
      });
      if (bad.length) msg(bad[0], 'error');
      if (!good.length) return;
      if (good.length > room) { good = good.slice(0, room); msg('Only ' + room + ' more image(s) fit in this project.', 'error'); }

      // Carry the rejection through: uploadToProject clears the message on
      // start, so in edit mode the "too large" line was written and wiped in
      // the same tick and the designer thought every file had landed.
      if (editingId) { uploadToProject(editingId, good, bad[0] || ''); return; }
      good.forEach(function (f) { staged.push({ file: f, url: URL.createObjectURL(f) }); });
      renderThumbs();
    }

    function releaseStaged() {
      staged.forEach(function (s) { try { URL.revokeObjectURL(s.url); } catch (e) {} });
      staged = [];
    }

    // ── Thumbnails ──
    function renderThumbs() {
      if (!els.thumbs) return;
      els.thumbs.innerHTML = '';
      var items = editingId
        ? (byId(editingId) || { images: [] }).images.map(function (i) { return { id: i.id, url: thumbUrl(i.url, 240) }; })
        : staged.map(function (s, i) { return { id: 'staged-' + i, url: s.url, staged: true }; });

      items.forEach(function (it, idx) {
        var t = el('div', { className: 'pfe-thumb', attrs: { draggable: 'true', tabindex: '0' } });
        t.appendChild(el('img', { attrs: { src: it.url, alt: '' } }));
        if (idx === 0) t.appendChild(el('span', { className: 'pfe-cover-tag', text: 'cover' }));

        var x = el('button', { className: 'pfe-x', attrs: { type: 'button', 'aria-label': 'Remove image' } });
        x.addEventListener('click', function (e) {
          e.stopPropagation();
          if (it.staged) {
            try { URL.revokeObjectURL(staged[idx].url); } catch (err) {}
            staged.splice(idx, 1);
            renderThumbs();
          } else {
            guarded(function () {
              return json({ intent: 'removeImage', imageId: it.id }).then(function (res) {
                if (!res.ok) { msg(res.data.error || 'Could not remove that image.', 'error'); return load(); }
                return load();
              });
            })();
          }
        });
        t.appendChild(x);

        // Drag to reorder: image 0 is the cover everywhere else on the site,
        // so the order here is a real decision, not decoration.
        t.addEventListener('dragstart', function (e) {
          t.classList.add('is-dragging');
          try { e.dataTransfer.setData('text/plain', String(idx)); e.dataTransfer.effectAllowed = 'move'; } catch (err) {}
        });
        t.addEventListener('dragend', function () { t.classList.remove('is-dragging'); });
        t.addEventListener('dragover', function (e) { e.preventDefault(); });
        t.addEventListener('drop', function (e) {
          e.preventDefault(); e.stopPropagation();
          // The strip sits right under the dropzone and looks like part of it,
          // so files land here often. They used to vanish without a word.
          if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
            acceptFiles(e.dataTransfer.files);
            return;
          }
          var from = parseInt(e.dataTransfer.getData('text/plain'), 10);
          if (isNaN(from) || from === idx) return;
          moveImage(from, idx);
        });
        // Keyboard reorder. The thumb is a focus stop with a focus ring, so it
        // has to do something; the cover image is decided here and mouse-only
        // was not an acceptable answer.
        t.addEventListener('keydown', function (e) {
          var delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
          if (!delta) return;
          e.preventDefault();
          var to = idx + delta;
          if (to < 0 || to >= items.length) return;
          moveImage(idx, to);
          var next = els.thumbs.children[to];
          if (next && next.focus) next.focus();
        });

        els.thumbs.appendChild(t);
      });

      if (els.thumbsHint) els.thumbsHint.hidden = items.length < 2;
    }

    function moveImage(from, to) {
      if (!editingId) {
        var it = staged.splice(from, 1)[0];
        staged.splice(to, 0, it);
        renderThumbs();
        return;
      }
      var p = byId(editingId);
      if (!p) return;
      // The repaint used to happen OUTSIDE the guard while the request happened
      // inside it, so a second drag before the first round trip repainted an
      // order the server never received - and image 0 is the cover everywhere
      // else on the site. Both now stand or fall together.
      if (busy) { msg('One moment, finishing the last change.', 'error'); return; }
      guarded(function () {
        var imgs = p.images.slice();
        var moved = imgs.splice(from, 1)[0];
        imgs.splice(to, 0, moved);
        p.images = imgs;               // optimistic, so the drag feels instant
        renderThumbs();
        return json({
          intent: 'reorderImages', projectId: editingId,
          imageIds: imgs.map(function (i) { return i.id; }),
        }).then(function (res) {
          if (!res.ok) { msg(res.data.error || 'Could not save the new order.', 'error'); return load(); }
        });
      })();
    }

    // Upload straight into an existing project, with a real progress ring.
    function uploadToProject(projectId, files, carryError) {
      var fd = new FormData();
      fd.append('projectId', projectId);
      files.forEach(function (f) { fd.append('images', f); });

      // Show placeholders for what is arriving so the ring has something to sit on.
      var placeholders = files.map(function (f) { return URL.createObjectURL(f); });
      var frag = placeholders.map(function (u) {
        var t = el('div', { className: 'pfe-thumb pfe-thumb--uploading' });
        t.appendChild(el('img', { attrs: { src: u, alt: '' } }));
        t.appendChild(el('span', { className: 'pfe-thumb-prog' }));
        var ring = el('span', { className: 'pfe-thumb-ring' });
        var pct = el('span', { className: 'pfe-thumb-pct', text: '0%' });
        t.appendChild(ring); t.appendChild(pct);
        els.thumbs.appendChild(t);
        return { ring: ring, pct: pct };
      });

      msg(carryError || '', carryError ? 'error' : null);
      uploading += files.length;
      setDropEnabled(false);
      lock(true);
      return upload(fd, function (ratio) {
        var p = Math.round(ratio * 100);
        frag.forEach(function (f) {
          f.pct.textContent = p + '%';
          f.ring.style.background = 'conic-gradient(var(--pfe-accent, #f0f0f0) ' + (p * 3.6) + 'deg, rgba(255,255,255,.22) 0)';
        });
      }).then(function (res) {
        uploading -= files.length;
        setDropEnabled(true);
        lock(false);
        if (!res.ok) {
          msg(res.data.error || 'Upload failed.', 'error');
          renderThumbs();
          placeholders.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (e) {} });
          return;
        }
        // Repaint FIRST, then revoke. The other order left the placeholders on
        // screen pointing at URLs that had already been revoked whenever the
        // reload that was meant to replace them failed.
        return load().then(function () {
          if (carryError) msg(carryError, 'error');
          placeholders.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (e) {} });
        });
      });
    }

    // ── Form <-> project ──
    function byId(id) {
      for (var i = 0; i < projects.length; i++) if (projects[i].id === id) return projects[i];
      return null;
    }

    function resetForm() {
      editingId = null;
      releaseStaged();
      els.title.value = '';
      if (els.role) els.role.value = '';
      if (els.year) els.year.value = '';
      if (els.client) els.client.value = '';
      if (els.clientPublic) els.clientPublic.checked = false;
      if (els.description) els.description.value = '';
      serviceCS.setValue([]);
      sourceCS.setValue('own');
      syncSourceHint();
      if (creditsCS) { creditsCS.setValue([]); renderChips(); }
      els.rights.checked = false;
      els.rights.disabled = false;
      if (els.formTitle) els.formTitle.textContent = labels.addTitle;
      els.submit.textContent = labels.addBtn;
      if (els.cancel) els.cancel.hidden = true;
      if (els.editNote) els.editNote.hidden = true;
      if (els.title) els.title.dispatchEvent(new Event('input'));
      if (els.description) els.description.dispatchEvent(new Event('input'));
      msg('');
      dirty = false;
      renderThumbs();
      paint();
    }

    function startEdit(id, quiet) {
      var p = byId(id);
      if (!p) return;
      releaseStaged();
      editingId = id;
      els.title.value = p.title || '';
      if (els.role) els.role.value = p.role || '';
      if (els.year) els.year.value = p.year || '';
      if (els.client) els.client.value = p.clientName || '';
      if (els.clientPublic) els.clientPublic.checked = !!p.clientNamePublic;
      if (els.description) els.description.value = p.description || '';
      // `serviceTags` first, the old single field as the fallback for a
      // project saved before the list existed.
      serviceCS.setValue((p.serviceTags && p.serviceTags.length)
        ? p.serviceTags.slice()
        : (p.serviceTag ? [p.serviceTag] : []));
      sourceCS.setValue(p.source || 'own');
      syncSourceHint();
      if (creditsCS) {
        // The owner is in `credits` too, first and flagged. They are credited by
        // being the owner, so they are not one of the picker's choices.
        creditsCS.setValue((p.credits || [])
          .filter(function (c) { return c && !c.owner; })
          .map(function (c) { return c.authorId; }));
        renderChips();
      }
      // Rights were already warranted for this project. Show it ticked and lock
      // it: unticking would silently unpublish the piece, which is a decision
      // that belongs in Delete, not in a checkbox.
      els.rights.checked = !!p.rightsConfirmed;
      els.rights.disabled = !!p.rightsConfirmed;
      if (els.formTitle) els.formTitle.textContent = labels.editTitle;
      els.submit.textContent = labels.saveBtn;
      if (els.cancel) els.cancel.hidden = false;
      // Recomputed on every fill, including the re-hydrate after a save: the
      // server flips an approved project to pending, and the note used to keep
      // claiming it was live.
      if (els.editNote) els.editNote.hidden = p.status !== 'approved';
      if (els.title) els.title.dispatchEvent(new Event('input'));
      if (els.description) els.description.dispatchEvent(new Event('input'));
      // A quiet re-hydrate happens right after a failed save, so it must not
      // wipe the message explaining the failure. Only an explicit Edit clears.
      if (!quiet) msg('');
      dirty = false;
      renderThumbs();
      paint();
      // Only when the designer asked for it. A re-hydrate after saving would
      // otherwise yank the page to the top on every save.
      if (!quiet && inner.scrollIntoView) inner.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function formValues() {
      return {
        title: (els.title.value || '').trim(),
        description: els.description ? els.description.value : '',
        serviceTags: serviceCS.getValue(),
        role: els.role ? els.role.value : '',
        year: els.year && els.year.value ? Number(els.year.value) : null,
        clientName: els.client ? els.client.value : '',
        clientNamePublic: !!(els.clientPublic && els.clientPublic.checked),
        source: sourceCS.getValue() === 'studio' ? 'studio' : 'own',
        // Was missing, so a project whose rights flag was false could never be
        // fixed here: the box demanded a tick, the save ignored it, and Send
        // for review then refused with no way forward.
        rightsConfirmed: !!(els.rights && els.rights.checked),
      };
    }

    // ── Project cards ──
    function itemHtml(p) {
      var cover = p.coverUrl || (p.images[0] && p.images[0].url);
      var services = (p.serviceTags && p.serviceTags.length)
        ? p.serviceTags.join(', ')
        : (p.serviceTag || '');
      var meta = [services, p.role, p.year].filter(Boolean).join(' · ');

      // WHOSE PROJECT THIS IS.
      //
      // A designer now sees the pieces they are CREDITED on as well as the
      // ones they submitted, because being credited is what puts a piece on
      // their public page and a portal that hides it leaves them unable to see
      // what their own page is about to show.
      //
      // They cannot change those. The project belongs to whoever submitted it:
      // two people editing one set of images from two portals is a race with
      // no winner, and a credit is not a claim of ownership. `canEdit` comes
      // from the server, which refuses the write regardless - this only stops
      // the page offering a button that would fail.
      //
      // `!== false` and not a truthiness test: an older payload has no such
      // field at all, and "did not say" must not read as "not allowed".
      var mine = p.canEdit !== false;
      var owner = null;
      for (var c = 0; c < (p.credits || []).length; c++) {
        if (p.credits[c] && p.credits[c].owner) { owner = p.credits[c]; break; }
      }
      var canSend = mine && (p.status === 'draft' || p.status === 'rejected');

      return '<article class="pfe-item' + (p.id === editingId ? ' is-editing' : '') +
        (mine ? '' : ' pfe-item--theirs') + '" data-pfe-item="' + esc(p.id) + '">' +
        '<div class="pfe-item-cover">' +
          (cover
            ? '<img loading="lazy" src="' + esc(thumbUrl(cover, 520)) + '" alt="">'
            : '<span class="pfe-item-noimg">No images yet</span>') +
          (p.images.length > 1 ? '<span class="pfe-item-count">' + p.images.length + '</span>' : '') +
        '</div>' +
        '<div class="pfe-item-body">' +
          '<span class="pfe-item-title">' + esc(p.title) + '</span>' +
          (meta ? '<span class="pfe-item-meta">' + esc(meta) + '</span>' : '') +
          '<span class="pfe-status pfe-status--' + esc(p.status) + '">' + esc(STATUS_TEXT[p.status] || p.status) + '</span>' +
          (p.featuredOnStudio ? '<span class="pfe-featured">Featured by the studio</span>' : '') +
          '<span class="pfe-item-meta">' + esc(STATUS_HINT[p.status] || '') + '</span>' +
          (p.reviewNote && mine ? '<span class="pfe-item-note">' + esc(p.reviewNote) + '</span>' : '') +
          // Whose it is, said plainly, and only when it is not theirs. Naming
          // the owner on every card would just repeat the reader's own name.
          (mine || !owner ? ''
            : '<span class="pfe-item-owner">' +
                esc(labels.byOther.replace('%s', owner.displayName)) + '</span>') +
        '</div>' +
        '<div class="pfe-item-actions">' +
          (mine
            ? '<button type="button" class="pfe-btn-ghost" data-pfe-edit="' + esc(p.id) + '">Edit</button>'
            : '<span class="pfe-item-meta">' + esc(labels.theirsHint) + '</span>') +
          (canSend ? '<button type="button" class="pfe-btn-quiet" data-pfe-send="' + esc(p.id) + '">Send for review</button>' : '') +
          (mine ? '<button type="button" class="pfe-btn-quiet is-danger pfe-spacer" data-pfe-del="' + esc(p.id) + '">Delete</button>' : '') +
        '</div>' +
      '</article>';
    }

    function paint() {
      if (!projects.length) {
        els.list.innerHTML = '';
        if (els.empty) els.empty.hidden = false;
        return;
      }
      if (els.empty) els.empty.hidden = true;
      els.list.innerHTML = projects.map(itemHtml).join('');
      bindList();
    }

    function guarded(fn) {
      return function () {
        // Saying nothing made a dropped click look like a dead button, and the
        // designer clicked the next thumbnail's x believing the first worked.
        if (busy) { msg('One moment, finishing the last change.', 'error'); return; }
        busy = true;
        var done = function () { busy = false; };
        var out;
        try { out = fn.apply(this, arguments); } catch (e) { done(); return; }
        if (out && out.then) out.then(done, done); else done();
      };
    }

    function bindList() {
      els.list.querySelectorAll('[data-pfe-edit]').forEach(function (b) {
        b.addEventListener('click', function () {
          // Not guarded() - that would swallow it silently. But it must not run
          // while a create is uploading, or the create's success handler resets
          // the form out from under the edit that just started.
          if (busy) { msg('One moment, finishing the last change.', 'error'); return; }
          // Switching wipes staged files and typed metadata. Ask first.
          if ((dirty || staged.length) && !editingId
            && !window.confirm('Discard the project you are adding?')) return;
          startEdit(b.getAttribute('data-pfe-edit'));
        });
      });
      els.list.querySelectorAll('[data-pfe-send]').forEach(function (b) {
        b.addEventListener('click', guarded(function () {
          return json({ intent: 'submit', projectId: b.getAttribute('data-pfe-send') }).then(function (res) {
            // load() on failure too: the usual reason is that a moderator
            // changed it underneath, and the card must stop showing the old
            // status with buttons that will keep failing.
            if (!res.ok) { banner(res.data.error || 'Could not send it for review.', 'error'); return load(); }
            banner('Sent. We will look at it shortly.', 'ok');
            return load();
          });
        }));
      });
      els.list.querySelectorAll('[data-pfe-del]').forEach(function (b) {
        b.addEventListener('click', guarded(function () {
          if (!window.confirm('Delete this project and its images? This cannot be undone.')) return;
          var id = b.getAttribute('data-pfe-del');
          return json({ intent: 'delete', projectId: id }).then(function (res) {
            if (!res.ok) { banner(res.data.error || 'Could not delete it.', 'error'); return load(); }
            if (editingId === id) resetForm();
            return load();
          });
        }));
      });
    }

    /** Shut the intake while an upload runs, so the limit cannot be raced. */
    function setDropEnabled(on) {
      if (els.files) els.files.disabled = !on;
      if (els.drop) {
        if (on) els.drop.classList.remove('is-disabled');
        else els.drop.classList.add('is-disabled');
      }
    }

    /**
     * Push the credit list for a project that now exists.
     *
     * Its own request because it is its own thing: the project write may
     * succeed while this fails, and saying "saved" for one and not the other
     * is better than pretending a partial save was whole. Skipped entirely
     * when nothing is picked and nothing was picked before, so the common case
     * costs no round trip.
     */
    function saveCredits(projectId) {
      if (!projectId || !creditsCS) return Promise.resolve(null);
      var ids = creditsCS.getValue();
      var had = editingId ? ((byId(editingId) || { credits: [] }).credits || [])
        .filter(function (c) { return c && !c.owner; }).map(function (c) { return c.authorId; }) : [];
      if (!ids.length && !had.length) return Promise.resolve(null);
      return json({ intent: 'setCredits', projectId: projectId, authorIds: ids });
    }

    // ── Load ──
    function load() {
      return fetch(API, { credentials: 'same-origin' }).then(read).then(function (res) {
        if (!res.ok) {
          banner(res.data.error || 'Could not load your portfolio.', 'error');
          // Still repaint. Returning here left placeholder thumbnails on screen
          // pointing at revoked blob URLs, so the strip froze on broken images.
          renderThumbs();
          return;
        }
        projects = res.data.projects || [];
        if (editingId && !byId(editingId)) {
          // It was deleted or moderated away underneath us. Say so, rather than
          // blanking the form and leaving the designer wondering.
          banner('That project is no longer there. It was removed while you had it open.', 'error');
          resetForm();
        } else if (editingId) {
          // Re-hydrate from the server: it clamps the year, trims the title and
          // may have flipped an approved project back to pending, and the form
          // used to keep showing what was typed rather than what was stored.
          startEdit(editingId, true);
        }
        paint();
        renderThumbs();
      }).catch(function () {
        banner('Could not reach the server. Try reloading.', 'error');
        renderThumbs();
      });
    }

    // ── Dropzone ──
    if (els.drop) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        els.drop.addEventListener(ev, function (e) { e.preventDefault(); els.drop.classList.add('is-dragover'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        els.drop.addEventListener(ev, function (e) { e.preventDefault(); els.drop.classList.remove('is-dragover'); });
      });
      els.drop.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) acceptFiles(e.dataTransfer.files);
      });
      els.drop.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && els.files) { e.preventDefault(); els.files.click(); }
      });
    }
    if (els.files) {
      els.files.addEventListener('change', function () {
        acceptFiles(els.files.files);
        els.files.value = '';
      });
    }

    // ── Save ──
    els.submit.addEventListener('click', guarded(function () {
      var v = formValues();
      if (!v.title) { msg('Give the project a title.', 'error'); els.title.focus(); return; }
      // The one check here that is not a formality. The server refuses too;
      // this just explains it without a round trip.
      if (!els.rights.checked) {
        msg('Confirm you have the right to show this work before saving.', 'error');
        return;
      }

      if (editingId) {
        lock(true);
        msg('Saving...');
        var savingId = editingId;
        // A promise chain does not stop when one link reports a failure, so the
        // outcome is tracked explicitly. Returning `load()` from the first step
        // let the second step run on and announce "Saved." for a save that had
        // just failed.
        return json({ intent: 'update', projectId: savingId, data: v })
          .then(function (res) {
            if (!res.ok) return { failed: res.data.error || 'Could not save it.' };
            return saveCredits(savingId).then(function (cr) {
              if (cr && cr.ok === false) return { failed: cr.data.error || 'Could not save the credits.' };
              return { failed: null };
            });
          })
          .then(function (outcome) {
            lock(false);
            if (outcome.failed) { msg(outcome.failed, 'error'); return load(); }
            banner('Saved.', 'ok');
            // Only if the project survived the reload; otherwise load() has
            // already blanked the form and said why.
            return load().then(function () { if (editingId) msg('Saved.', 'ok'); });
          });
      }

      var fd = new FormData();
      fd.append('title', v.title);
      fd.append('description', v.description);
      // One entry per service. The server reads them with getAll.
      (v.serviceTags || []).forEach(function (t) { fd.append('serviceTags', t); });
      fd.append('role', v.role);
      fd.append('year', v.year == null ? '' : String(v.year));
      fd.append('clientName', v.clientName);
      fd.append('clientNamePublic', v.clientNamePublic ? 'true' : 'false');
      fd.append('source', v.source);
      fd.append('rightsConfirmed', 'true');
      staged.forEach(function (s) { fd.append('images', s.file); });

      lock(true);
      msg(staged.length ? 'Uploading...' : 'Saving...');
      return upload(fd, function (ratio) {
        if (staged.length) msg('Uploading ' + Math.round(ratio * 100) + '%');
      }).then(function (res) {
        lock(false);
        if (!res.ok) {
          msg(res.data.error || 'Could not save it.', 'error');
          // The server commits the project first and attaches images second, so
          // a rejected image leaves a real project behind. Reload and open it,
          // otherwise it is invisible and every retry makes another one until
          // the 40-project ceiling is full of phantom drafts.
          if (res.data.partial && res.data.project && res.data.project.id) {
            var newId = res.data.project.id;
            releaseStaged();
            return load().then(function () {
              startEdit(newId, true);
              msg(res.data.error || 'The project was saved, but the images were not. Add them here.', 'error');
            });
          }
          return;
        }
        // Credits are a second write: the project has to exist first, and the
        // multipart create is what brings it into existence.
        var newId = res.data.project && res.data.project.id;
        return saveCredits(newId).then(function () {
          // Guard the reset: if an Edit started while this was uploading, the
          // unconditional reset used to wipe it and say "Added."
          if (!editingId) resetForm();
          banner('Added. Send it for review when it looks right.', 'ok');
          return load();
        });
      });
    }));

    if (els.cancel) {
      els.cancel.addEventListener('click', function () { resetForm(); banner(''); });
    }

    renderThumbs();
    load();
  }

  function boot() {
    if (inEditor()) return;
    document.querySelectorAll('.pfe-wrap').forEach(function (root) {
      // The <script src> lives inside the section, so two copies of the section
      // load it twice and every wrap would be initialised twice. Each init has
      // its own `busy`, so the re-entrancy guard cannot help: one click on Add
      // project fired up to four creates.
      if (root.getAttribute('data-pfe-init') === '1') return;
      root.setAttribute('data-pfe-init', '1');
      try { init(root); } catch (e) { try { console.error('[pfe]', e); } catch (e2) {} }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
